import React, { useEffect, useState } from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Alert, Platform } from 'react-native';
import AppTextInput from './AppTextInput';
import {
  clearPayPalDirectUrl,
  getSavedPayPalDirectUrl,
  savePayPalDirectUrl,
} from '../services/paypalDirectPaymentService';
import { maskPayPalDirectUrl } from '../utils/paypalDirectPayment';
import {
  SELLER_PAYPAL_SETUP_HELP,
  SELLER_PAYPAL_CONFIRM_HELP,
} from '../constants/paymentHelpText';

const showUserAlert = (title: string, message: string) => {
  if (Platform.OS === 'web') {
    (globalThis as { alert?: (msg: string) => void }).alert?.(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message);
  }
};

const confirmUserAction = (
  title: string,
  message: string,
  onConfirm: () => void
) => {
  if (Platform.OS === 'web') {
    if (
      (globalThis as { confirm?: (msg: string) => boolean }).confirm?.(
        `${title}\n\n${message}`
      )
    ) {
      onConfirm();
    }
    return;
  }
  Alert.alert(title, message, [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Remove', style: 'destructive', onPress: onConfirm },
  ]);
};

interface PayPalDirectSetupModalProps {
  visible: boolean;
  businessId: string;
  onClose: () => void;
  onSaved: () => void;
}

const PayPalDirectSetupModal: React.FC<PayPalDirectSetupModalProps> = ({
  visible,
  businessId,
  onClose,
  onSaved,
}) => {
  const [input, setInput] = useState('');
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || !businessId) {
      return;
    }
    let cancelled = false;
    (async () => {
      const url = await getSavedPayPalDirectUrl(businessId);
      if (!cancelled) {
        setCurrentUrl(url);
        setInput('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, businessId]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const result = await savePayPalDirectUrl(businessId, input);
      if (!result.success) {
        showUserAlert('Invalid PayPal link', result.error || 'Could not save.');
        return;
      }
      setCurrentUrl(result.url || null);
      onSaved();
      const successMessage =
        'Customers will pay you directly on PayPal. BookAPT does not process or hold these payments.';
      if (Platform.OS === 'web') {
        showUserAlert('PayPal linked', successMessage);
        onClose();
      } else {
        Alert.alert('PayPal linked', successMessage, [{ text: 'OK', onPress: onClose }]);
      }
    } catch {
      showUserAlert('Error', 'Could not save PayPal link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    confirmUserAction(
      'Remove PayPal link?',
      'Customers will no longer see a Pay with PayPal option for your business.',
      async () => {
        setLoading(true);
        try {
          await clearPayPalDirectUrl(businessId);
          setCurrentUrl(null);
          setInput('');
          onSaved();
          onClose();
        } catch {
          showUserAlert('Error', 'Could not remove PayPal link. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    );
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text style={styles.title}>PayPal (direct to you)</Text>
          <Text style={styles.body}>{SELLER_PAYPAL_SETUP_HELP}</Text>
          <Text style={styles.bodySecondary}>{SELLER_PAYPAL_CONFIRM_HELP}</Text>

          {currentUrl ? (
            <Text style={styles.current}>
              Current: {maskPayPalDirectUrl(currentUrl)}
            </Text>
          ) : null}

          <AppTextInput
            style={styles.input}
            placeholder="e.g. mybusiness or paypal.me/mybusiness"
            value={input}
            onChangeText={setInput}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType={Platform.OS === 'ios' ? 'url' : 'default'}
            editable={!loading}
          />

          <TouchableOpacity
            style={[styles.primary, loading && styles.disabled]}
            onPress={handleSave}
            disabled={loading || !input.trim()}
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.primaryText}>Save PayPal link</Text>
            )}
          </TouchableOpacity>

          {currentUrl ? (
            <TouchableOpacity style={styles.remove} onPress={handleRemove} disabled={loading}>
              <Text style={styles.removeText}>Remove PayPal link</Text>
            </TouchableOpacity>
          ) : null}

          <TouchableOpacity style={styles.cancel} onPress={onClose} disabled={loading}>
            <Text style={styles.cancelText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111',
    marginBottom: 8,
  },
  body: {
    fontSize: 14,
    color: '#555',
    lineHeight: 20,
    marginBottom: 8,
  },
  bodySecondary: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
    marginBottom: 12,
  },
  current: {
    fontSize: 13,
    color: '#0070ba',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 10,
    padding: 12,
    fontSize: 16,
    marginBottom: 16,
  },
  primary: {
    backgroundColor: '#0070ba',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  primaryText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  disabled: {
    opacity: 0.6,
  },
  remove: {
    marginTop: 12,
    alignItems: 'center',
  },
  removeText: {
    color: '#c00',
    fontSize: 15,
  },
  cancel: {
    marginTop: 16,
    alignItems: 'center',
  },
  cancelText: {
    color: '#666',
    fontSize: 15,
  },
});

export default PayPalDirectSetupModal;
